import React, { useEffect, useRef, useState } from 'react';
import { colors, fonts } from './styles';

const population = 'Cystic fibrosis · adults · US';

const opener =
  "Hello! I'm here to help you figure out what you'd like to learn from patients. Before we get started, a quick note: Pulse Checks are designed to give directional signal from patients — not exhaustive research. We'll do our best to uncover the areas you're most curious about. What would you like to explore?";

const script = [
  { role: 'ai', text: opener },
  {
    role: 'user',
    text:
      'We want to understand how long-term stable patients feel about switching from our twice-daily therapy to the new once-daily option.',
  },
  {
    role: 'ai',
    text: 'Good starting point. Are you more interested in what would motivate a switch, what might hold them back — or both?',
  },
  { role: 'user', text: "Both, but especially the barriers. We're seeing slower conversion than we expected." },
  {
    role: 'ai',
    text:
      'Got it. Is the slower conversion showing up across the board, or concentrated in specific groups — like patients who have been stable for years on the current regimen?',
  },
  { role: 'user', text: 'Mostly the long-stable group. They are hesitant even when we explain the benefits.' },
  { role: 'ai', text: "That's a clear direction. Here's what I'd bring to patients:" },
  {
    role: 'topic',
    topic: {
      summary:
        'Barriers to switching from twice-daily to once-daily therapy among long-term stable patients',
      audience: 'Verified CF adults in the US',
      subtopics: [
        'Specific hesitations among patients stable on their current regimen',
        'What would reduce the perceived risk of switching',
        'How current care routines create comfort or friction',
        'Language and framing that resonates vs creates pushback',
      ],
    },
  },
];

const TypingIndicator = () => (
  <div
    style={{
      display: 'flex',
      gap: '4px',
      padding: '12px 16px',
      borderRadius: '14px',
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderBottomLeftRadius: '4px',
      alignItems: 'center',
    }}
  >
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        style={{
          width: '7px',
          height: '7px',
          borderRadius: '50%',
          backgroundColor: colors.deepCoral,
          display: 'inline-block',
          animation: `pi-tdot 900ms ${i * 150}ms infinite ease-in-out`,
        }}
      />
    ))}
  </div>
);

const Avatar = () => (
  <div
    aria-hidden="true"
    style={{
      width: '28px',
      height: '28px',
      minWidth: '28px',
      borderRadius: '14px',
      background: `linear-gradient(135deg, ${colors.coral} 0%, ${colors.terracotta} 100%)`,
      color: colors.white,
      fontSize: '10px',
      fontWeight: 700,
      fontFamily: fonts.sans,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '8px',
      marginTop: '2px',
      letterSpacing: '0.5px',
    }}
  >
    AI
  </div>
);

const AIBubble = ({ text, showLabel }) => (
  <div style={{ marginBottom: '12px' }}>
    {showLabel && (
      <div
        style={{
          fontFamily: fonts.sans,
          fontSize: '11px',
          color: colors.midGray,
          marginBottom: '4px',
          marginLeft: '36px',
          fontWeight: 500,
        }}
      >
        AI Researcher
      </div>
    )}
    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
      <Avatar />
      <div
        style={{
          maxWidth: '82%',
          padding: '12px 16px',
          borderRadius: '14px',
          borderBottomLeftRadius: '4px',
          background: colors.white,
          border: `1px solid ${colors.border}`,
          color: colors.charcoal,
          fontFamily: fonts.sans,
          fontSize: '13.5px',
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
        }}
      >
        {text}
      </div>
    </div>
  </div>
);

const UserBubble = ({ text }) => (
  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
    <div
      style={{
        maxWidth: '82%',
        padding: '12px 16px',
        borderRadius: '14px',
        borderBottomRightRadius: '4px',
        backgroundColor: colors.charcoal,
        color: colors.white,
        fontFamily: fonts.sans,
        fontSize: '13.5px',
        lineHeight: 1.6,
        whiteSpace: 'pre-wrap',
      }}
    >
      {text}
    </div>
  </div>
);

const TopicConfirmed = ({ topic }) => (
  <div
    style={{
      padding: '18px 20px',
      borderRadius: '12px',
      background:
        'linear-gradient(135deg, rgba(250,190,192,0.28) 0%, rgba(245,215,170,0.22) 50%, rgba(181,201,168,0.18) 100%), #fff',
      border: '1px solid rgba(235,160,155,0.35)',
      marginBottom: '12px',
      textAlign: 'left',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
      <span
        aria-hidden="true"
        style={{
          width: '22px',
          height: '22px',
          borderRadius: '50%',
          backgroundColor: colors.coral,
          color: colors.white,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          fontWeight: 700,
        }}
      >
        ✓
      </span>
      <span
        style={{
          fontFamily: fonts.sans,
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '1.5px',
          color: colors.deepCoral,
          textTransform: 'uppercase',
        }}
      >
        Topic confirmed
      </span>
    </div>
    <div
      style={{
        fontFamily: fonts.serif,
        fontSize: '16px',
        fontStyle: 'italic',
        fontWeight: 500,
        color: colors.charcoal,
        lineHeight: 1.4,
        marginBottom: '12px',
      }}
    >
      {topic.summary}
    </div>
    {topic.audience && (
      <div style={{ marginBottom: '12px' }}>
        <div
          style={{
            fontFamily: fonts.sans,
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '1.2px',
            color: colors.deepCoral,
            textTransform: 'uppercase',
            marginBottom: '4px',
          }}
        >
          Sending to
        </div>
        <div
          style={{
            fontFamily: fonts.sans,
            fontSize: '13px',
            color: colors.charcoal,
            fontWeight: 500,
            lineHeight: 1.5,
          }}
        >
          {topic.audience}
        </div>
      </div>
    )}
    <ul style={{ margin: '0 0 14px 18px', padding: 0, listStyle: 'disc' }}>
      {topic.subtopics.map((s, i) => (
        <li
          key={i}
          style={{
            fontFamily: fonts.sans,
            fontSize: '13px',
            color: colors.warmGray,
            lineHeight: 1.55,
            marginBottom: '4px',
          }}
        >
          {s}
        </li>
      ))}
    </ul>
    <button
      type="button"
      disabled
      aria-hidden="true"
      style={{
        width: '100%',
        padding: '10px 14px',
        borderRadius: '10px',
        border: 'none',
        background: colors.coral,
        color: colors.white,
        fontFamily: fonts.sans,
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'default',
      }}
    >
      Use this topic →
    </button>
  </div>
);

export const AIResearcherDemo = () => {
  const [started, setStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [aiTyping, setAiTyping] = useState(false);
  const [userTyping, setUserTyping] = useState('');
  const containerRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || started) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStarted(true);
      },
      { threshold: 0.2 }
    );
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [started]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages.length, aiTyping, userTyping]);

  useEffect(() => {
    if (!started) return;
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setMessages(script);
      return;
    }

    let cancelled = false;
    const timeouts = new Set();
    const wait = (ms) =>
      new Promise((resolve) => {
        const id = setTimeout(() => {
          timeouts.delete(id);
          if (!cancelled) resolve();
        }, ms);
        timeouts.add(id);
      });

    const run = async () => {
      while (!cancelled) {
        setMessages([]);
        setUserTyping('');
        setAiTyping(false);
        await wait(900);

        for (let i = 0; i < script.length; i++) {
          if (cancelled) return;
          const step = script[i];

          if (step.role === 'ai' || step.role === 'topic') {
            setAiTyping(true);
            const think = step.role === 'topic'
              ? 1800
              : Math.min(2200, 650 + (step.text || '').length * 10);
            await wait(think);
            if (cancelled) return;
            setAiTyping(false);
            setMessages((m) => [...m, step]);
            await wait(500);
          } else if (step.role === 'user') {
            await wait(850);
            const chars = step.text;
            for (let j = 1; j <= chars.length; j++) {
              if (cancelled) return;
              setUserTyping(chars.slice(0, j));
              await wait(22 + Math.random() * 28);
            }
            await wait(550);
            if (cancelled) return;
            setUserTyping('');
            setMessages((m) => [...m, step]);
            await wait(400);
          }
        }

        await wait(8000);
      }
    };

    run();

    return () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
    };
  }, [started]);

  const firstAiIndex = messages.findIndex((m) => m.role === 'ai');

  return (
    <div
      ref={containerRef}
      style={{
        maxWidth: '640px',
        margin: '0 auto',
        backgroundColor: colors.white,
        borderRadius: '16px',
        border: `1px solid ${colors.border}`,
        boxShadow: '0 12px 48px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        textAlign: 'left',
      }}
    >
      <style>{`
        @keyframes pi-tdot {
          0%, 60%, 100% { opacity: 0.25; transform: translateY(0); }
          30% { opacity: 1; transform: translateY(-4px); }
        }
        @keyframes pi-online-pulse {
          0% { box-shadow: 0 0 0 0 rgba(107,154,122,0.6); }
          70% { box-shadow: 0 0 0 6px rgba(107,154,122,0); }
          100% { box-shadow: 0 0 0 0 rgba(107,154,122,0); }
        }
        @keyframes pi-caret { 50% { opacity: 0; } }
        @keyframes pi-bubble-in {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .pi-bubble { animation: pi-bubble-in 0.35s ease both; }
      `}</style>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '14px 18px',
          borderBottom: `1px solid ${colors.border}`,
          backgroundColor: colors.offWhite,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: '180px' }}>
          <div
            style={{
              fontFamily: fonts.sans,
              fontSize: '13px',
              fontWeight: 600,
              color: colors.charcoal,
              lineHeight: 1.2,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: '#6B9A7A',
                animation: 'pi-online-pulse 2.4s infinite',
                display: 'inline-block',
              }}
            />
            AI Researcher
          </div>
          <div
            style={{
              fontFamily: fonts.sans,
              fontSize: '11px',
              color: colors.midGray,
              marginTop: '3px',
            }}
          >
            What would you like to learn?
          </div>
        </div>
        <div
          style={{
            fontFamily: fonts.sans,
            fontSize: '10px',
            fontWeight: 600,
            letterSpacing: '0.8px',
            color: colors.deepCoral,
            textTransform: 'uppercase',
            padding: '4px 10px',
            borderRadius: '12px',
            backgroundColor: colors.coralLight,
            border: `1px solid ${colors.coralMid}`,
          }}
        >
          {population}
        </div>
      </div>

      {/* Messages */}
      <div
        ref={messagesEndRef}
        style={{
          padding: '18px',
          height: '420px',
          overflowY: 'auto',
          backgroundColor: colors.offWhite,
          scrollBehavior: 'smooth',
        }}
      >
        {messages.map((m, i) => {
          if (m.role === 'topic') {
            return (
              <div key={i} className="pi-bubble">
                <TopicConfirmed topic={m.topic} />
              </div>
            );
          }
          if (m.role === 'ai') {
            return (
              <div key={i} className="pi-bubble">
                <AIBubble text={m.text} showLabel={i === firstAiIndex} />
              </div>
            );
          }
          return (
            <div key={i} className="pi-bubble">
              <UserBubble text={m.text} />
            </div>
          );
        })}
        {aiTyping && (
          <div className="pi-bubble" style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
            <Avatar />
            <TypingIndicator />
          </div>
        )}
      </div>

      {/* Input */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 14px',
          borderTop: `1px solid ${colors.border}`,
          backgroundColor: colors.white,
        }}
      >
        <div
          aria-hidden="true"
          style={{
            flex: 1,
            backgroundColor: colors.offWhite,
            border: `1px solid ${colors.border}`,
            borderRadius: '10px',
            padding: '10px 14px',
            fontFamily: fonts.sans,
            fontSize: '13px',
            color: userTyping ? colors.charcoal : colors.midGray,
            minHeight: '40px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {userTyping || 'Describe what you want to understand…'}
          {userTyping && (
            <span
              aria-hidden="true"
              style={{
                display: 'inline-block',
                width: '2px',
                height: '14px',
                backgroundColor: colors.charcoal,
                marginLeft: '2px',
                animation: 'pi-caret 1s step-end infinite',
              }}
            />
          )}
        </div>
        <button
          type="button"
          disabled
          aria-hidden="true"
          style={{
            border: 'none',
            borderRadius: '10px',
            padding: '10px 16px',
            backgroundColor: userTyping ? colors.terracotta : colors.border,
            color: userTyping ? colors.white : colors.midGray,
            fontFamily: fonts.sans,
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'default',
            transition: 'background-color 0.2s ease, color 0.2s ease',
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
};
